import type { Listing } from '@shared/types';
import { normalizeCity } from './areaSlug';

/** Major browse destinations shown in search / home / nav */
export const BROWSE_CITIES = [
  'Cape Town',
  'Durban',
  'Johannesburg',
  'Pretoria',
  'Port Elizabeth',
] as const;

export type BrowseCity = (typeof BROWSE_CITIES)[number];

const KZN_ALIASES = new Set(
  [
    'kwazulu-natal',
    'kwa zulu natal',
    'kzn',
    'natal',
    'durban',
    'umhlanga',
    'ballito',
    'salt rock',
    'shakas rock',
    'chakas rock',
    'umdloti',
    'la lucia',
    'u mhlanga',
  ].map(normalizeCity)
);

const CAPE_TOWN_ALIASES = new Set(
  ['cape town', 'cpt', 'western cape', 'v & a waterfront', 'va waterfront', 'waterfront'].map(
    normalizeCity
  )
);

const JOBURG_ALIASES = new Set(
  ['johannesburg', 'joburg', 'jhb', 'gauteng', 'sandton', 'rosebank'].map(normalizeCity)
);

const PRETORIA_ALIASES = new Set(['pretoria', 'tshwane'].map(normalizeCity));

const PE_ALIASES = new Set(
  ['port elizabeth', 'gqeberha', 'eastern cape', 'pe'].map(normalizeCity)
);

function tagArea(listing: Listing): string {
  const areaTag = (listing.tags || []).find((t) => t.startsWith('area:'));
  return areaTag ? areaTag.slice(5).trim() : '';
}

/** True when a location field is empty or the literal "Unknown" from imports. */
export function isPlaceholderLocation(value: string | undefined): boolean {
  const norm = normalizeCity(value || '');
  return !norm || norm === 'unknown';
}

/** Comma-separated location line for listing pages and cards (no "Unknown"). */
export function formatListingLocationLine(listing: Listing): string {
  const display = getListingDisplayLocation(listing);
  const loc = listing.location || ({} as Listing['location']);
  const parts: string[] = [];

  const address = (loc.address || '').trim();
  if (
    address &&
    !isPlaceholderLocation(address) &&
    normalizeCity(address) !== normalizeCity(display.city) &&
    normalizeCity(address) !== normalizeCity(display.area || '')
  ) {
    parts.push(address);
  }

  if (
    display.area &&
    !parts.some((p) => normalizeCity(p) === normalizeCity(display.area!))
  ) {
    parts.push(display.area);
  }

  if (display.city && !parts.some((p) => normalizeCity(p) === normalizeCity(display.city))) {
    parts.push(display.city);
  }

  if (display.country) {
    parts.push(display.country);
  }

  return parts.join(', ');
}

function collectLocationTokens(listing: Listing): string[] {
  const loc = listing.location || ({} as Listing['location']);
  const tokens = [
    loc.city,
    loc.region,
    loc.address,
    tagArea(listing),
    ...(listing.tags || []).map((t) => t.replace(/^(area|category|type):/, '')),
  ]
    .filter((t): t is string => Boolean(t))
    .map((t) => normalizeCity(t));

  return [...new Set(tokens)];
}

/** Map imported province / suburb data to a browse city (e.g. KZN coast → Durban). */
export function resolveBrowseCity(raw: string): string {
  const norm = normalizeCity(raw);
  if (!norm || norm === 'unknown') return '';

  if (KZN_ALIASES.has(norm) || [...KZN_ALIASES].some((a) => norm.includes(a) || a.includes(norm))) {
    return 'Durban';
  }
  if (
    CAPE_TOWN_ALIASES.has(norm) ||
    [...CAPE_TOWN_ALIASES].some((a) => norm.includes(a) || a.includes(norm))
  ) {
    return 'Cape Town';
  }
  if (
    JOBURG_ALIASES.has(norm) ||
    [...JOBURG_ALIASES].some((a) => norm.includes(a) || a.includes(norm))
  ) {
    return 'Johannesburg';
  }
  if (PRETORIA_ALIASES.has(norm) || norm.includes('pretoria')) return 'Pretoria';
  if (PE_ALIASES.has(norm) || norm.includes('port elizabeth') || norm.includes('gqeberha')) {
    return 'Port Elizabeth';
  }

  const titleCase = raw.trim();
  const browseMatch = BROWSE_CITIES.find((c) => normalizeCity(c) === norm);
  return browseMatch || titleCase;
}

/** Best city label for cards and detail pages (never "Unknown" when area/region exists). */
export function getListingDisplayLocation(listing: Listing): {
  city: string;
  area?: string;
  country: string;
} {
  const loc = listing.location || ({} as Listing['location']);
  const area = tagArea(listing);
  const cityRaw = (loc.city || '').trim();
  const cityNorm = normalizeCity(cityRaw);

  let city = cityRaw;
  if (!cityNorm || cityNorm === 'unknown') {
    city = resolveBrowseCity(loc.region || '') || resolveBrowseCity(area) || area || 'South Coast';
  } else if (KZN_ALIASES.has(cityNorm) || normalizeCity(loc.region || '') === 'kwazulu-natal') {
    city = area || 'Durban';
  } else {
    city = resolveBrowseCity(cityRaw) || cityRaw;
  }

  return {
    city,
    ...(area && normalizeCity(area) !== normalizeCity(city) ? { area } : {}),
    country: loc.country || 'South Africa',
  };
}

/** Whether a listing belongs under a browse destination (Durban, Cape Town, etc.). */
export function listingMatchesBrowseCity(listing: Listing, browseCity: string): boolean {
  const target = normalizeCity(browseCity);
  if (!target) return true;

  const display = getListingDisplayLocation(listing);
  if (normalizeCity(display.city) === target) return true;
  if (display.area && normalizeCity(display.area) === target) return true;

  const tokens = collectLocationTokens(listing);
  const resolvedBrowse = resolveBrowseCity(browseCity);
  const resolvedTarget = normalizeCity(resolvedBrowse);

  if (tokens.some((t) => t === target || t === resolvedTarget)) return true;

  if (target === 'durban') {
    return tokens.some((t) => KZN_ALIASES.has(t) || [...KZN_ALIASES].some((a) => t.includes(a)));
  }
  if (target === 'cape town') {
    return tokens.some((t) => CAPE_TOWN_ALIASES.has(t));
  }
  if (target === 'johannesburg') {
    return tokens.some((t) => JOBURG_ALIASES.has(t));
  }
  if (target === 'pretoria') {
    return tokens.some((t) => PRETORIA_ALIASES.has(t));
  }
  if (target === 'port elizabeth') {
    return tokens.some((t) => PE_ALIASES.has(t));
  }

  return tokens.some((t) => t.includes(target) || target.includes(t));
}

/** Group key for nav / area pages (prefer browse city over raw province). */
export function getListingAreaGroupKey(listing: Listing): string {
  const display = getListingDisplayLocation(listing);
  return normalizeCity(display.city);
}

export function getListingAreaGroupLabel(listing: Listing): string {
  return getListingDisplayLocation(listing).city;
}
